import { useDispatch, useSelector, useStore } from "react-redux";

import { useMount } from "app/custom-hooks/useMount";
import { selectSfSettings } from "app/pages/sales-intelligence/sub-modules/management/store/selectors/salesforce/exportSelectors";
import type { CrmColumnItem } from "app/pages/sales-intelligence/sub-modules/management/types";
import type { RootState } from "single-spa/store/types";

import useCrmEnrichmentJob from "../../../hooks/wizard/useCrmEnrichmentJob";
import { setMappings, setSelectedJobObject } from "../../../store/action-creators";
import { selectJobState, selectMappings, selectWizardFilters } from "../../../store/selectors";
import type { CrmEnrichmentJobDTO } from "../../../types/types";
import { getDefaultMappings } from "../../../utils/defaultMappings";
import { ENRICHMENT_OBJECT_TO_EXPORT_NAME } from "../../../utils/enrichmentObjectToExportName";
import { createJobDTO } from "../../../utils/job";
import { selectSfColumnsByJobObject } from "../store/selectors";

type HookParams = {
    initialJob?: CrmEnrichmentJobDTO;
    columns: readonly CrmColumnItem[];
};

type HookValue = {
    createJobDTO: () => ReturnType<typeof createJobDTO>;
    initializeFiltersFromJob: (job?: CrmEnrichmentJobDTO) => void;
};

const useSfEnrichmentJob = ({ initialJob, columns }: HookParams): HookValue => {
    const dispatch = useDispatch();
    const store = useStore<RootState>();

    const initialColumns = useSelector((state: RootState) =>
        selectSfColumnsByJobObject(state, initialJob?.enrichmentObject),
    );

    useMount(() => {
        dispatch(setSelectedJobObject("company"));

        if (initialJob?.mappings) {
            dispatch(setMappings(initialJob.mappings));
        } else {
            const enrichmentObject = initialJob?.enrichmentObject ?? "company";
            const sfSettings = selectSfSettings(store.getState());
            const exportSettings = sfSettings[ENRICHMENT_OBJECT_TO_EXPORT_NAME[enrichmentObject]];
            dispatch(setMappings(getDefaultMappings("salesforce", enrichmentObject, exportSettings)));
        }
    });

    const { initializeFiltersFromJob } = useCrmEnrichmentJob({
        columns,
        initialColumns,
        initialJob,
    });

    const getJobDTO = () => {
        const state = store.getState();
        const currentJobState = selectJobState(state);
        const wizardFilters = selectWizardFilters(state);
        const mappings = selectMappings(state);

        return createJobDTO({
            ...currentJobState,
            ...wizardFilters,
            columns,
            initialJob,
            mappings,
        });
    };

    return {
        createJobDTO: getJobDTO,
        initializeFiltersFromJob,
    };
};

export default useSfEnrichmentJob;
